<script lang="ts">
  /* THE DESKTOP AND TABLET NAV — the one place a panel is chosen from.
   *
   * Rendered from the registry, so this file names no panel and knows about no
   * feature. Adding a planning surface puts a button here and a slot in the
   * dock, and neither file is edited.
   *
   * ═══ AND AN UNAVAILABLE ENTRY SAYS WHY ═══
   *
   * Same rule the layers panel and the layer bar follow: a control that
   * silently does nothing is worse than one that greys out with a reason. A
   * An unavailable panel stays operable so a tap can show its reason.
   */
  import Icon from '../primitives/Icon.svelte';
  import { panels } from './panels.svelte';

  const items = $derived(panels.nav());

</script>

<nav class="rail" aria-label="panels">
  {#each items as p (p.id)}
    {@const reason = p.reason?.() ?? null}
    {@const off = p.available?.() === false}
    <button
      class="slot"
      class:on={panels.is(p.id)}
      class:warn={p.tone?.() === 'warn'}
      class:off
      title={off && reason ? `${p.title} — ${reason}` : p.title}
      aria-label={off && reason ? `${p.title} — ${reason}` : p.title}
      aria-disabled={off ? 'true' : undefined}
      aria-pressed={panels.is(p.id)}
      onclick={() => panels.toggle(p.id)}
    >
      <span class="ic">
        <!-- A live reading REPLACES the mark — see `readout` in the registry.
             The time entry shows "06:40" and the tools entry shows the armed
             tool's label, because in both cases the state is what you needed to
             see without opening anything. -->
        {#if p.readout?.()}
          <span class="readout">{p.readout()}</span>
        {:else}
          <Icon name={p.icon} />
        {/if}
      </span>
      <span class="lb">{p.title}</span>
    </button>
  {/each}
</nav>

<style>
  .rail {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--sp-2);
    padding: var(--sp-3) 0;
    background: var(--s-1);
    border-right: 1px solid var(--line);
    overflow-y: auto;
    scrollbar-width: none;
  }
  .rail::-webkit-scrollbar {
    display: none;
  }

  .slot {
    width: 44px;
    height: 44px;
    flex: none;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    border: 1px solid transparent;
    border-radius: var(--r-2);
    background: none;
    color: var(--sub);
    cursor: pointer;
    transition:
      color var(--dur-fast) var(--ease-out),
      background var(--dur-fast) var(--ease-out);
  }
  .slot:hover:not(:disabled) {
    color: var(--ink);
    background: var(--s-2);
  }
  .slot:focus-visible {
    outline: 2px solid var(--cy);
    outline-offset: 2px;
  }
  .ic {
    font: 16px/1 var(--mono);
  }
  /* Seven pixels of Silkscreen is small, and it is deliberate: the glyph is the
     target and the word is the confirmation. Without it the rail is a memory
     test — which is the exact complaint that put the quick-toggle bar's twelve
     unlabelled glyphs into a sheet with real names on a phone. */
  .lb {
    font: 7px/1 var(--px);
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .slot.on {
    color: var(--cy);
    border-color: var(--cy-line);
    background: var(--cy-weak);
  }
  /* Amber outranks teal here. A parked time cursor is a state of the WHOLE
     chart, and it stays visible whether or not its panel is the open one. */
  .slot.warn {
    color: var(--am);
    border-color: var(--am-line);
    background: var(--am-weak);
  }
  .slot.off {
    opacity: 0.35;
    cursor: default;
  }
</style>
